import { useEffect, useState, useMemo, useCallback } from 'react';
import { GetSlotsInput, Search } from '@kyruus/search-sdk';
import type { PMCConfig } from '@kyruus/types';
import {
  getCommonTrackingParams,
  SEARCH_SHUFFLE_TOKEN,
  getTokenValue
} from 'Common/logging/tokens';
import { transformFilterUrlParamsForSearchSdk } from '../../../behaviors/search-v9/actions/utils';
import {
  AggregatedPurposeOptionsByRelationship,
  AggregatedSlot,
  ProviderMatchProviderV9
} from '@kyruus/types';
import {
  getProviderLocationIdsByPrimaryLocation,
  mapSlotFacetsToAggregatedSlots,
  mapSlotFacetsToAggregatedPurposes
} from '../utils';
import { AggregatedAvailabilitySlotsRequest } from '@kyruus/scheduling-components';

interface UseLocationAvailabilityParams {
  config: PMCConfig;
  primaryLocationId: string;
  customerCode: string;
  productName: string;
  schedulingVisibility: string;
  providerSearchQuery?: Record<string, unknown>;
}

interface UseLocationAvailabilityOutput {
  locationProviders: ProviderMatchProviderV9[];
  locationIds: number[];
  searchClient: Search;
  loading: boolean;
  error: Error | null;
  fetchAvailabilitySlots: (
    slotsRequest: AggregatedAvailabilitySlotsRequest
  ) => Promise<AggregatedSlot[]>;
  fetchPurposeOptions: (
    locationIds: number[]
  ) => Promise<AggregatedPurposeOptionsByRelationship>;
}

const DEFAULT_SEARCH_QUERY = {
  per_page: 100
};

export const useAggregatedAvailability = ({
  config,
  primaryLocationId,
  customerCode,
  productName,
  schedulingVisibility,
  providerSearchQuery: searchQuery = DEFAULT_SEARCH_QUERY
}: UseLocationAvailabilityParams): UseLocationAvailabilityOutput => {
  const [locationProviders, setLocationProviders] = useState<
    ProviderMatchProviderV9[]
  >([]);
  const [locationIds, setLocationIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Create search client once and reuse
  const searchClient = useMemo(() => {
    return new Search({
      customerCode,
      productName,
      baseURL: '/api/searchservice-v9'
    });
  }, [customerCode, productName]);

  useEffect(() => {
    let cancelled = false;

    const fetchProviders = async () => {
      if (cancelled) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const fullParams = {
          ...getCommonTrackingParams({ config }),
          ...searchQuery,
          shuffle_seed:
            getTokenValue({ token: SEARCH_SHUFFLE_TOKEN }) || undefined,
          filter: transformFilterUrlParamsForSearchSdk([
            `locations.primary_marketable_location_id:${primaryLocationId}`
          ]),
          provider_fields: ['-clinical_keywords'].join(',')
        };

        const searchResponse = await searchClient.getProviders(fullParams);

        if (cancelled) {
          return;
        }

        // TODO: Update search-sdk to use @kyruus/types instead of custom types where it makes sense.
        const providerResults = searchResponse.data._result;
        const mappedProviders = providerResults.map(
          (provider) => provider.provider as unknown as ProviderMatchProviderV9
        );

        const extractedLocationIds = getProviderLocationIdsByPrimaryLocation(
          providerResults,
          primaryLocationId
        );

        setLocationProviders(mappedProviders);
        setLocationIds(extractedLocationIds);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch providers')
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProviders();

    return () => {
      cancelled = true;
    };
  }, [config, primaryLocationId, searchClient, searchQuery]);

  const fetchAvailabilitySlots = useCallback(
    async (
      slotsRequest: AggregatedAvailabilitySlotsRequest
    ): Promise<AggregatedSlot[]> => {
      const [visibilityKey, visibilityValue] = schedulingVisibility.split(':');

      const getSlotsInput: GetSlotsInput = {
        facet: [['location_id', 'date', 'start', 'provider_id']],
        filter: {
          and: {
            or: {
              provider_id: slotsRequest.providerIds
            },
            and: {
              location_id: slotsRequest.locationIds,
              [`visibilities.${visibilityKey}`]: [visibilityValue],
              patient_relationship: [slotsRequest.patientRelationship],
              purpose: [slotsRequest.purpose.name]
            }
          }
        }
      };

      const slotsResponse = await searchClient.getSlots(getSlotsInput);
      const aggregatedSlots = mapSlotFacetsToAggregatedSlots(
        slotsResponse.data.facets,
        slotsRequest
      );

      return aggregatedSlots;
    },
    [schedulingVisibility, searchClient]
  );

  const fetchPurposeOptions = useCallback(
    async (
      locationIds: number[]
    ): Promise<AggregatedPurposeOptionsByRelationship> => {
      const [visibilityKey, visibilityValue] = schedulingVisibility.split(':');

      const getPurposesInput: GetSlotsInput = {
        facet: [
          ['patient_relationship', 'purpose', 'visit_method', 'provider_id']
        ],
        filter: {
          and: {
            and: {
              location_id: locationIds,
              [`visibilities.${visibilityKey}`]: [visibilityValue]
            }
          }
        }
      };

      const slotsResponse = await searchClient.getSlots(getPurposesInput);
      const purposes = mapSlotFacetsToAggregatedPurposes(
        slotsResponse.data.facets
      );

      return purposes;
    },
    [schedulingVisibility, searchClient]
  );

  return {
    locationProviders,
    locationIds,
    searchClient,
    loading,
    error,
    fetchAvailabilitySlots,
    fetchPurposeOptions
  };
};
